import dayjs from 'dayjs';
import type { AnnouncementAudience, AnnouncementSeverity } from '../types/SystemAnnouncement';

export type AnnouncementTemplateId = 'maintenance' | 'systemRestart' | 'refresh' | 'newFeature';

export interface AnnouncementFormState {
  title: string;
  message: string;
  severity: AnnouncementSeverity;
  audience: AnnouncementAudience;
  targetUsernames: string;
  startsAt: string;
  endsAt: string;
  dismissible: boolean;
  featureName: string;
}

export interface AnnouncementTemplateOption {
  id: AnnouncementTemplateId;
  label: string;
  description: string;
}

export const ANNOUNCEMENT_TEMPLATES: AnnouncementTemplateOption[] = [
  {
    id: 'maintenance',
    label: 'Scheduled maintenance',
    description: 'Warn users about maintenance between a start and end time.',
  },
  {
    id: 'systemRestart',
    label: 'System restart',
    description: 'Warn users before a server restart or deployment outage window.',
  },
  {
    id: 'refresh',
    label: 'Please refresh browser',
    description: 'Ask users to refresh after a deployment.',
  },
  {
    id: 'newFeature',
    label: 'New feature',
    description: 'Tell users about a newly available feature.',
  },
];

export function emptyAnnouncementForm(): AnnouncementFormState {
  return {
    title: '',
    message: '',
    severity: 'INFO',
    audience: 'ALL',
    targetUsernames: '',
    startsAt: '',
    endsAt: '',
    dismissible: true,
    featureName: '',
  };
}

export function defaultMaintenanceWindow(): { startsAt: string; endsAt: string } {
  const start = dayjs().add(1, 'day').hour(2).minute(0).second(0).millisecond(0);
  const end = start.add(2, 'hour');
  return {
    startsAt: start.format('YYYY-MM-DDTHH:mm'),
    endsAt: end.format('YYYY-MM-DDTHH:mm'),
  };
}

export function buildMaintenanceMessage(startsAt: string, endsAt: string): string {
  const startLabel = dayjs(startsAt).format('MMM D, YYYY HH:mm');
  const endLabel = dayjs(endsAt).format('MMM D, YYYY HH:mm');
  return `Narralizer will undergo scheduled maintenance from ${startLabel} to ${endLabel}. Please save your work and finish edits before maintenance begins.`;
}

/** Default restart window: ~5 minutes from now, ~20 minutes total. */
export function defaultSystemRestartWindow(): { startsAt: string; endsAt: string } {
  const start = dayjs().add(5, 'minute').second(0).millisecond(0);
  const end = start.add(15, 'minute');
  return {
    startsAt: start.format('YYYY-MM-DDTHH:mm'),
    endsAt: end.format('YYYY-MM-DDTHH:mm'),
  };
}

export function buildSystemRestartMessage(startsAt: string, endsAt: string): string {
  const startLabel = dayjs(startsAt).format('MMM D, YYYY HH:mm');
  const endLabel = dayjs(endsAt).format('MMM D, YYYY HH:mm');
  return `Narralizer will restart for a system update from ${startLabel} to ${endLabel}. Please save your work and finish open edits. You may be signed out briefly during the restart. After service is back, refresh your browser (Ctrl+R or Cmd+R) if anything looks out of date.`;
}

export function buildNewFeatureMessage(featureName: string): { title: string; message: string } {
  const name = featureName.trim();
  return {
    title: name ? `New: ${name}` : 'New feature available',
    message: name
      ? `New in Narralizer: ${name}. Open Help from the toolbar to learn more.`
      : 'A new feature is available in Narralizer. Open Help from the toolbar to learn more.',
  };
}

export function buildAnnouncementFormFromTemplate(
  templateId: AnnouncementTemplateId,
): AnnouncementFormState {
  if (templateId === 'maintenance') {
    const { startsAt, endsAt } = defaultMaintenanceWindow();
    return {
      title: 'Scheduled maintenance',
      message: buildMaintenanceMessage(startsAt, endsAt),
      severity: 'WARNING',
      audience: 'ALL',
      targetUsernames: '',
      startsAt,
      endsAt,
      dismissible: false,
      featureName: '',
    };
  }

  if (templateId === 'systemRestart') {
    const { startsAt, endsAt } = defaultSystemRestartWindow();
    return {
      title: 'System restart',
      message: buildSystemRestartMessage(startsAt, endsAt),
      severity: 'WARNING',
      audience: 'ALL',
      targetUsernames: '',
      startsAt,
      endsAt,
      dismissible: false,
      featureName: '',
    };
  }

  if (templateId === 'newFeature') {
    return {
      title: 'New feature available',
      message: buildNewFeatureMessage('').message,
      severity: 'INFO',
      audience: 'ALL',
      targetUsernames: '',
      startsAt: '',
      endsAt: '',
      dismissible: true,
      featureName: '',
    };
  }

  return {
    title: 'Please refresh your browser',
    message:
      'We deployed changes to Narralizer. Please refresh your browser (Ctrl+R or Cmd+R) if anything looks out of date or stops working.',
    severity: 'INFO',
    audience: 'ALL',
    targetUsernames: '',
    startsAt: '',
    endsAt: '',
    dismissible: true,
    featureName: '',
  };
}
